import { useState, Suspense, lazy } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, Settings, ArrowLeft, Edit, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import { User } from '@/types/User';
import { toast } from 'sonner';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useUserRoles } from '@/hooks/useUserRoles';
import { getInitials } from '@/utils/userUtils';
import { getApiEndpoint } from '@/utils/ERPUtils';
import SuspenseLoader from '@/components/common/SuspenseLoader';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';

import { logger } from '@/utils/logger';
const EditUserDialog = lazy(() => import('@/pages/admin/users/components/EditUserDialog').then(module => ({ default: module.EditUserDialog })));
const CreateUserDialog = lazy(() => import('@/pages/admin/users/components/CreateUserDialog').then(module => ({ default: module.CreateUserDialog })));
const UserDetailDialog = lazy(() => import('@/pages/admin/users/components/UserDetailDialog').then(module => ({ default: module.UserDetailDialog })));
const UserFilters = lazy(() => import('@/components/user-management/UserFilters').then(module => ({ default: module.UserFilters })));

export const MobileUserManagement: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);

  const {
    roles,
    filteredUsers,
    loading,
    filters,
    updateFilters,
    fetchUsers
  } = useUserManagement();

  const { user, loading: authLoading } = useAuth();

  // Get user roles to check if user is admin
  const { roles: userRoles, isSuperAdmin } = useUserRoles();
  const isAdmin = isSuperAdmin || userRoles.some(role => role.name === 'Admin');

  const handleAllUsersClick = () => {
    setShowUserList(true);
  };

  // const handleAddUserClick = () => {
  //   setShowCreateDialog(true);
  // };

  const handleBackToMenu = () => {
    setShowUserList(false);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleUserUpdated = () => {
    // fetchUsers();
    setShowEditDialog(false);
    setSelectedUser(null);
    toast.success('User updated successfully');
  };

  const handleDeleteUser = async (userEmail: string, userName: string) => {
    try {
      const response = await axios.post(
        getApiEndpoint('DELETE_USER'),
        { user: userEmail },
        { withCredentials: true }
      );

      const data = response.data?.message;
      if (data?.status === 'error') {
        throw new Error(data?.message || 'Failed to delete user');
      }

      toast.success(`User "${userName}" deleted successfully`);
      fetchUsers(); // Refresh the user list
    } catch (error: any) {
      logger.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
    }
  };

  // Mobile User List View
  if (showUserList) {
    return (
      <div className="h-full w-full max-w-full flex flex-col bg-background overflow-hidden">
        {/* Header Section */}
        <div className="flex-shrink-0 px-4 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToMenu}
              className="text-muted-foreground hover:bg-muted"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold text-foreground text-center">
              All Users
            </h1>
            {/* Placeholder for centering */}
            <div className="w-10" />
          </div>

          {/* Create User Button */}
          <div className="flex justify-center my-4">
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-primary hover:bg-primary/85 text-primary-foreground px-8 py-2 rounded-md"
            >
              New User
            </Button>
          </div>
          
          {/* Filters and Search */}
          <Suspense fallback={<SuspenseLoader type="minimal" height="h-16" />}>
            <UserFilters
              filters={filters}
              roles={roles}
              onFiltersChange={updateFilters}
            />
          </Suspense>
        </div>

        {/* User List */}
        <ScrollArea className="flex-1 px-4">
          {loading || authLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-foreground" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="space-y-3 pb-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center px-4 py-3 bg-muted rounded-lg"
                >
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="h-full w-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium rounded-full">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>

                  <div
                    className="flex-1 min-w-0 ml-3 cursor-pointer"
                    onClick={() => {
                      setSelectedUser(user);
                      setShowUserDetail(true)
                    }}
                  >
                    <h3 className="text-foreground font-medium truncate">{user.name}</h3>
                    <p className="text-muted-foreground text-sm truncate">{user.email}</p>
                    {user.roles.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.roles.slice(0, 2).map((role) => (
                          <Badge key={role} variant="outline" className="text-xs border-border text-muted-foreground bg-background">
                            {role}
                          </Badge>
                        ))}
                        {user.roles.length > 2 && (
                          <Badge variant="outline" className="text-xs border-border text-muted-foreground bg-background">
                            +{user.roles.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="w-8 h-8 min-w-8 min-h-8 aspect-square rounded-full bg-background flex items-center justify-center active:bg-accent flex-shrink-0"
                    >
                      <Edit className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="w-8 h-8 min-w-8 min-h-8 aspect-square rounded-full bg-background flex items-center justify-center active:bg-accent flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4 text-foreground" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="dark bg-background text-foreground w-[calc(100%-32px)] max-w-md rounded-xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete User</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{user.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-500 hover:bg-red-600"
                            onClick={() => handleDeleteUser(user.email, user.name)}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Users className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center mb-2">No users found</p>
              <p className="text-muted-foreground text-sm text-center">
                Try adjusting your filters
              </p>
            </div>
          )}
        </ScrollArea>

        {/* Dialogs */}
        <Suspense fallback={null}>
          <CreateUserDialog
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
            onUserCreated={() => {
              fetchUsers();
              setShowCreateDialog(false)
            }}
          />
        </Suspense>
        <EditUserDialog
          isCurrentUser={user?.id == selectedUser?.id}
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          user={selectedUser}
          onDataRefresh={fetchUsers}
          onUserUpdated={handleUserUpdated}
        />
        {selectedUser && (
          <Suspense fallback={null}>
            <UserDetailDialog
              isCurrentUser={user?.id == selectedUser?.id}
              isAdmin={isAdmin}
              user={selectedUser}
              open={showUserDetail}
              onOpenChange={setShowUserDetail}
              onUserUpdated={() => {
                fetchUsers();
                setShowUserDetail(false)
              }}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // Main Menu View
  return (
    <div className="h-full w-full max-w-full flex flex-col bg-background overflow-hidden">
      {/* Header Section */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        <h1 className="text-2xl font-semibold text-foreground text-center mb-2">
          User Management
        </h1>
        <p className="text-md text-muted-foreground text-center font-normal">
          Manage users, roles, and permissions
        </p>
      </div>

      {/* Menu Options */}
      <ScrollArea className="flex-1 px-4">
        <div className="space-y-3 pb-4">
          <div
            onClick={handleAllUsersClick}
            className="flex items-center justify-between px-4 py-4 bg-muted rounded-lg cursor-pointer active:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
                <Users className="h-5 w-5 text-foreground" />
              </div>
              <span className="text-foreground font-medium">All Users</span>
            </div>
            <ChevronRight className="h-6 w-6 text-foreground" />
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};